const SITE_BASE = (process.env.REACT_APP_API_BASE_URL || window.location.origin).replace(/\/$/, "");
const API_BASE = SITE_BASE.endsWith("/api") ? SITE_BASE : `${SITE_BASE}/api`;

export type WeatherMode = "off" | "advisory" | "adaptive";
export type WeatherStatus = "disabled" | "available" | "unavailable" | "outside_forecast";

export interface WeatherCondition {
  code: number;
  group: string;
  description: string;
  icon_code: string;
  icon_url: string;
}

export interface WeatherAttribution {
  provider: string;
  text: string;
  url: string;
  logo_url: string;
}

export interface WeatherCacheState {
  cached: boolean;
  stale: boolean;
  fetched_at: string;
}

export interface WeatherCurrent {
  observed_at: string;
  timezone_offset_seconds: number;
  temperature_c: number;
  feels_like_c: number;
  temperature_min_c: number;
  temperature_max_c: number;
  humidity_percent: number;
  pressure_hpa: number;
  visibility_m: number;
  cloud_cover_percent: number;
  wind_speed_mps: number;
  wind_direction_degrees: number;
  wind_gust_mps: number | null;
  rain_mm_1h: number;
  snow_mm_1h: number;
  sunrise_at: string;
  sunset_at: string;
  condition: WeatherCondition;
}

export interface WeatherForecastPeriod {
  at: string;
  temperature_c: number;
  feels_like_c: number;
  temperature_min_c: number;
  temperature_max_c: number;
  humidity_percent: number;
  precipitation_probability: number;
  rain_mm_3h: number;
  snow_mm_3h: number;
  wind_speed_mps: number;
  wind_direction_degrees: number;
  condition: WeatherCondition;
}

export interface WeatherForecastDay {
  date: string;
  temperature_min_c: number;
  temperature_max_c: number;
  humidity_percent_average: number;
  precipitation_probability_max: number;
  rain_mm_total: number;
  snow_mm_total: number;
  wind_speed_max_mps: number;
  condition: WeatherCondition;
  periods: WeatherForecastPeriod[];
}

export interface WeatherSource extends WeatherAttribution {
  current: WeatherCacheState | null;
  forecast: WeatherCacheState | null;
  warnings: string[];
}

export interface WeatherData {
  location: {
    selector_type: "city" | "place" | "coordinates";
    name: string;
    city_id: number | null;
    place_id: string | null;
    latitude: number;
    longitude: number;
  };
  current: WeatherCurrent | null;
  forecast: {
    timezone_offset_seconds: number;
    days: WeatherForecastDay[];
  } | null;
  source: WeatherSource;
}

interface WeatherResponse {
  success: boolean;
  data?: WeatherData;
  error?: {
    type?: string;
    title?: string;
    message?: string;
  };
}

export class WeatherApiError extends Error {
  status: number;
  type?: string;

  constructor(message: string, status: number, type?: string) {
    super(message);
    this.name = "WeatherApiError";
    this.status = status;
    this.type = type;
  }
}

export async function fetchCityWeather(
  cityId: number,
  days = 5,
  signal?: AbortSignal,
): Promise<WeatherData> {
  if (!Number.isInteger(cityId) || cityId <= 0) {
    throw new WeatherApiError("شناسه شهر معتبر نیست.", 400, "invalid_city_id");
  }
  if (!Number.isInteger(days) || days < 1 || days > 5) {
    throw new WeatherApiError("تعداد روزهای پیش‌بینی باید بین ۱ تا ۵ باشد.", 400, "invalid_days");
  }

  const params = new URLSearchParams({ city_id: String(cityId), days: String(days) });
  const response = await fetch(`${API_BASE}/weather/?${params.toString()}`, { signal });

  let body: WeatherResponse;
  try {
    body = await response.json();
  } catch {
    throw new WeatherApiError("پاسخ نامعتبر از سرویس آب‌وهوا دریافت شد.", response.status);
  }

  if (!response.ok || body.success === false || !body.data) {
    throw new WeatherApiError(
      body.error?.message || "اطلاعات آب‌وهوا در دسترس نیست.",
      response.status,
      body.error?.type,
    );
  }

  return body.data;
}

export async function fetchPlaceWeather(
  placeId: string,
  days = 5,
  signal?: AbortSignal,
): Promise<WeatherData> {
  if (!placeId.trim()) {
    throw new WeatherApiError("شناسه مکان معتبر نیست.", 400, "invalid_place_id");
  }
  if (!Number.isInteger(days) || days < 1 || days > 5) {
    throw new WeatherApiError("تعداد روزهای پیش‌بینی باید بین ۱ تا ۵ باشد.", 400, "invalid_days");
  }

  const params = new URLSearchParams({ place_id: placeId, days: String(days) });
  const response = await fetch(`${API_BASE}/weather/?${params.toString()}`, { signal });

  let body: WeatherResponse;
  try {
    body = await response.json();
  } catch {
    throw new WeatherApiError("پاسخ نامعتبر از سرویس آب‌وهوا دریافت شد.", response.status);
  }

  if (!response.ok || body.success === false || !body.data) {
    throw new WeatherApiError(
      body.error?.message || "اطلاعات آب‌وهوا در دسترس نیست.",
      response.status,
      body.error?.type,
    );
  }

  return body.data;
}
